import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type BusinessOverviewWindowMetric = {
    today: number;
    yesterday: number;
    currentWeekMondayToSunday: number;
};

export type BusinessOverviewHeadline = {
    registeredUsersToday: number;
    purchasedUsersToday: number;
    approximateSalesTodayInBaseCurrency: number;
    practiceAttemptsToday: number;
    mockTestsTakenToday: number;
    chatMessagesToday: number;
    uniqueActiveUsersToday: number;
};

export type BusinessOverviewCalendar = {
    todayLocalDate: string;
    yesterdayLocalDate: string;
    weekStartLocalDate: string;
    weekEndLocalDate: string;
};

export type BusinessOverviewSalesByCountry = {
    countryCode: string;
    currency: string;
    purchasedUsers: number;
    purchaseCount: number;
    totalAmount: number;
    approximateAmountInBaseCurrency: number;
};

export type BusinessOverviewSalesByCurrency = {
    currency: string;
    purchasedUsers: number;
    purchaseCount: number;
    totalAmount: number;
    approximateAmountInBaseCurrency: number;
};

export type BusinessOverviewOrderDetail = {
    paymentIntentId: string;
    userId: string;
    userName: string;
    userEmail: string;
    purchaseType: string;
    planName: string;
    status: string;
    billingCountryCode: string;
    billingCurrency: string;
    finalAmount: number;
    discountAmount: number;
    couponCode: string;
    providerPaymentId: string;
    createdAtUtc: string;
    updatedAtUtc: string;
};

export type BusinessOverviewCountrySnapshot = {
    countryCode: string;
    registeredUsers: BusinessOverviewWindowMetric;
    practiceAttempts: BusinessOverviewWindowMetric;
    mockTestsTaken: BusinessOverviewWindowMetric;
    chatMessages: BusinessOverviewWindowMetric;
    uniqueActiveUsers: BusinessOverviewWindowMetric;
};

export type BusinessOverviewHourlyTrendPoint = {
    timeLabel: string;
    today: number;
    yesterday: number;
};

export type BusinessOverviewData = {
    generatedAtUtc: string;
    baseCurrency: string;
    timezoneId: string;
    requestCountryCode: string;
    calendar: BusinessOverviewCalendar;
    headline: BusinessOverviewHeadline;
    registrations: {
        users: BusinessOverviewWindowMetric;
    };
    ordersToday: {
        distinctUsers: number;
        orderCount: number;
        returnedDetailCount: number;
        isTruncated: boolean;
        details: BusinessOverviewOrderDetail[];
    };
    salesToday: {
        purchasedUsers: number;
        purchaseCount: number;
        approximateTotalInBaseCurrency: number;
        distinctCountries: number;
        distinctCurrencies: number;
        byCountry: BusinessOverviewSalesByCountry[];
        byCurrency: BusinessOverviewSalesByCurrency[];
    };
    activity: {
        practiceAttempts: BusinessOverviewWindowMetric;
        mockTestsTaken: BusinessOverviewWindowMetric;
        chatMessages: BusinessOverviewWindowMetric;
        uniquePracticeUsers: BusinessOverviewWindowMetric;
        uniqueMockTestUsers: BusinessOverviewWindowMetric;
        uniqueChatUsers: BusinessOverviewWindowMetric;
        uniqueActiveUsers: BusinessOverviewWindowMetric;
    };
    hourlyTrends: {
        registrations: BusinessOverviewHourlyTrendPoint[];
        practiceAttempts: BusinessOverviewHourlyTrendPoint[];
    };
    countrySnapshots: BusinessOverviewCountrySnapshot[];
};

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string) {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string) {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin jobs request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeWindowMetric(value: unknown): BusinessOverviewWindowMetric {
    const record = asRecord(value);

    return {
        today: asNumber(record.today),
        yesterday: asNumber(record.yesterday),
        currentWeekMondayToSunday: asNumber(record.currentWeekMondayToSunday),
    };
}

function normalizeSalesByCountry(value: unknown): BusinessOverviewSalesByCountry {
    const record = asRecord(value);

    return {
        countryCode: asString(record.countryCode, 'UNRESOLVED'),
        currency: asString(record.currency),
        purchasedUsers: asNumber(record.purchasedUsers),
        purchaseCount: asNumber(record.purchaseCount),
        totalAmount: asNumber(record.totalAmount),
        approximateAmountInBaseCurrency: asNumber(record.approximateAmountInBaseCurrency),
    };
}

function normalizeSalesByCurrency(value: unknown): BusinessOverviewSalesByCurrency {
    const record = asRecord(value);

    return {
        currency: asString(record.currency),
        purchasedUsers: asNumber(record.purchasedUsers),
        purchaseCount: asNumber(record.purchaseCount),
        totalAmount: asNumber(record.totalAmount),
        approximateAmountInBaseCurrency: asNumber(record.approximateAmountInBaseCurrency),
    };
}

function normalizeOrderDetail(value: unknown): BusinessOverviewOrderDetail {
    const record = asRecord(value);

    return {
        paymentIntentId: asString(record.paymentIntentId),
        userId: asString(record.userId),
        userName: asString(record.userName),
        userEmail: asString(record.userEmail),
        purchaseType: asString(record.purchaseType),
        planName: asString(record.planName),
        status: asString(record.status),
        billingCountryCode: asString(record.billingCountryCode),
        billingCurrency: asString(record.billingCurrency),
        finalAmount: asNumber(record.finalAmount),
        discountAmount: asNumber(record.discountAmount),
        couponCode: asString(record.couponCode),
        providerPaymentId: asString(record.providerPaymentId),
        createdAtUtc: asString(record.createdAtUtc),
        updatedAtUtc: asString(record.updatedAtUtc),
    };
}

function normalizeCountrySnapshot(value: unknown): BusinessOverviewCountrySnapshot {
    const record = asRecord(value);

    return {
        countryCode: asString(record.countryCode, 'UNRESOLVED'),
        registeredUsers: normalizeWindowMetric(record.registeredUsers),
        practiceAttempts: normalizeWindowMetric(record.practiceAttempts),
        mockTestsTaken: normalizeWindowMetric(record.mockTestsTaken),
        chatMessages: normalizeWindowMetric(record.chatMessages),
        uniqueActiveUsers: normalizeWindowMetric(record.uniqueActiveUsers),
    };
}

function normalizeHourlyTrendPoint(value: unknown): BusinessOverviewHourlyTrendPoint {
    const record = asRecord(value);

    return {
        timeLabel: asString(record.timeLabel),
        today: asNumber(record.today),
        yesterday: asNumber(record.yesterday),
    };
}

function normalizeBusinessOverviewData(value: unknown): BusinessOverviewData {
    const record = asRecord(value);
    const headline = asRecord(record.headline);
    const calendar = asRecord(record.calendar);
    const registrations = asRecord(record.registrations);
    const ordersToday = asRecord(record.ordersToday);
    const salesToday = asRecord(record.salesToday);
    const activity = asRecord(record.activity);
    const hourlyTrends = asRecord(record.hourlyTrends);

    return {
        generatedAtUtc: asString(record.generatedAtUtc),
        baseCurrency: asString(record.baseCurrency, 'AUD'),
        timezoneId: asString(record.timezoneId, 'UTC'),
        requestCountryCode: asString(record.requestCountryCode),
        calendar: {
            todayLocalDate: asString(calendar.todayLocalDate),
            yesterdayLocalDate: asString(calendar.yesterdayLocalDate),
            weekStartLocalDate: asString(calendar.weekStartLocalDate),
            weekEndLocalDate: asString(calendar.weekEndLocalDate),
        },
        headline: {
            registeredUsersToday: asNumber(headline.registeredUsersToday),
            purchasedUsersToday: asNumber(headline.purchasedUsersToday),
            approximateSalesTodayInBaseCurrency: asNumber(headline.approximateSalesTodayInBaseCurrency),
            practiceAttemptsToday: asNumber(headline.practiceAttemptsToday),
            mockTestsTakenToday: asNumber(headline.mockTestsTakenToday),
            chatMessagesToday: asNumber(headline.chatMessagesToday),
            uniqueActiveUsersToday: asNumber(headline.uniqueActiveUsersToday),
        },
        registrations: {
            users: normalizeWindowMetric(registrations.users),
        },
        ordersToday: {
            distinctUsers: asNumber(ordersToday.distinctUsers),
            orderCount: asNumber(ordersToday.orderCount),
            returnedDetailCount: asNumber(ordersToday.returnedDetailCount),
            isTruncated: ordersToday.isTruncated === true,
            details: Array.isArray(ordersToday.details) ? ordersToday.details.map(normalizeOrderDetail) : [],
        },
        salesToday: {
            purchasedUsers: asNumber(salesToday.purchasedUsers),
            purchaseCount: asNumber(salesToday.purchaseCount),
            approximateTotalInBaseCurrency: asNumber(salesToday.approximateTotalInBaseCurrency),
            distinctCountries: asNumber(salesToday.distinctCountries),
            distinctCurrencies: asNumber(salesToday.distinctCurrencies),
            byCountry: Array.isArray(salesToday.byCountry)
                ? salesToday.byCountry.map(normalizeSalesByCountry)
                : [],
            byCurrency: Array.isArray(salesToday.byCurrency)
                ? salesToday.byCurrency.map(normalizeSalesByCurrency)
                : [],
        },
        activity: {
            practiceAttempts: normalizeWindowMetric(activity.practiceAttempts),
            mockTestsTaken: normalizeWindowMetric(activity.mockTestsTaken),
            chatMessages: normalizeWindowMetric(activity.chatMessages),
            uniquePracticeUsers: normalizeWindowMetric(activity.uniquePracticeUsers),
            uniqueMockTestUsers: normalizeWindowMetric(activity.uniqueMockTestUsers),
            uniqueChatUsers: normalizeWindowMetric(activity.uniqueChatUsers),
            uniqueActiveUsers: normalizeWindowMetric(activity.uniqueActiveUsers),
        },
        hourlyTrends: {
            registrations: Array.isArray(hourlyTrends.registrations)
                ? hourlyTrends.registrations.map(normalizeHourlyTrendPoint)
                : [],
            practiceAttempts: Array.isArray(hourlyTrends.practiceAttempts)
                ? hourlyTrends.practiceAttempts.map(normalizeHourlyTrendPoint)
                : [],
        },
        countrySnapshots: Array.isArray(record.countrySnapshots)
            ? record.countrySnapshots.map(normalizeCountrySnapshot)
            : [],
    };
}

async function requestAdminJobs<T>(path: string, init: RequestInit = {}, query?: Record<string, string>): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin jobs request failed'));
        }
    }

    return payload as T;
}

export async function getBusinessOverview(baseCurrency: string) {
    const response = unwrapAdminApiPayload(
        await requestAdminJobs<BusinessOverviewData | AdminApiEnvelope<BusinessOverviewData>>(
            'AdminJobs/get-app-progress',
            { method: 'GET' },
            { baseCurrency },
        ),
    );

    return normalizeBusinessOverviewData(response);
}
